/**
 * Approach 3: useReducer for UI state + explicit Image() prefetch pool (concurrency-capped).
 */
import { useEffect, useReducer, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useContextData } from '../../ApiContext';
import { MOB_PRODUCTS_PAGE_SIZE } from '../../Slider/SliderMobNew';
import { ProductGridSkeleton } from '../../Components/images/Mob/ProductGridSkeleton';
import { fetchMobProductsFirstPage } from './mobProductsFirstPage';
import './MobProductsReducerPrefetchPage.scss';

const AUTO_MS = 2200;

function prefetchImages(urls, concurrency = 4) {
    if (!urls.length) return Promise.resolve();
    return new Promise((resolve) => {
        const list = [...urls];
        let index = 0;
        let inFlight = 0;

        function doneOne() {
            inFlight--;
            kick();
            if (index >= list.length && inFlight === 0) resolve();
        }

        function kick() {
            while (inFlight < concurrency && index < list.length) {
                const u = list[index++];
                inFlight++;
                const im = new Image();
                im.onload = doneOne;
                im.onerror = doneOne;
                im.src = u;
            }
            if (index >= list.length && inFlight === 0) resolve();
        }

        kick();
    });
}

const initialState = {
    loading: false,
    error: null,
    items: [],
    activeId: null,
    slideById: {},
};

function reducer(state, action) {
    switch (action.type) {
        case 'FETCH_START':
            return { ...state, loading: true, error: null };
        case 'FETCH_SUCCESS':
            return {
                ...state,
                loading: false,
                items: action.items,
                activeId: null,
                slideById: {},
            };
        case 'FETCH_FAIL':
            return { ...state, loading: false, error: action.message, items: [] };
        case 'ACTIVATE': {
            const id = action.id;
            const togglingOff = state.activeId != null && String(state.activeId) === String(id);
            const nextActive = togglingOff ? null : id;
            const slideById = { ...state.slideById };
            if (nextActive) slideById[id] = 0;
            return { ...state, activeId: nextActive, slideById };
        }
        case 'TICK': {
            if (!state.activeId) return state;
            const item = state.items.find((i) => String(i.id) === String(state.activeId));
            if (!item?.urls?.length) return state;
            const cur = state.slideById[state.activeId] ?? 0;
            const next = (cur + 1) % item.urls.length;
            return {
                ...state,
                slideById: { ...state.slideById, [state.activeId]: next },
            };
        }
        default:
            return state;
    }
}

export function MobProductsReducerPrefetchPage() {
    const navigate = useNavigate();
    const { queryParams, setQueryParams } = useContextData();
    const [state, dispatch] = useReducer(reducer, initialState);
    const [queryParamsInit, setQueryParamsInit] = useState(false);

    useEffect(() => {
        setQueryParams({
            category: [],
            subcategory: [],
            price: [0, 10000],
        });
        setQueryParamsInit(true);
    }, [setQueryParams]);

    useEffect(() => {
        if (!queryParamsInit) return;
        let cancelled = false;
        const run = async () => {
            dispatch({ type: 'FETCH_START' });
            try {
                const mapped = await fetchMobProductsFirstPage(queryParams);
                if (cancelled) return;
                dispatch({ type: 'FETCH_SUCCESS', items: mapped });
            } catch (e) {
                if (!cancelled) {
                    dispatch({
                        type: 'FETCH_FAIL',
                        message: e?.response?.data?.message || e?.message || 'Failed to load',
                    });
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [queryParams, queryParamsInit]);

    useEffect(() => {
        if (!state.items.length) return;
        const flat = state.items.flatMap((p) => p.urls || []);
        prefetchImages(flat, 4).catch(() => {});
    }, [state.items]);

    useEffect(() => {
        if (!state.activeId) return;
        const id = setInterval(() => dispatch({ type: 'TICK' }), AUTO_MS);
        return () => clearInterval(id);
    }, [state.activeId]);

    const goPdp = (id) => navigate(`/specific/${id}`);

    return (
        <div className="mob-rp">
            <header className="mob-rp__header">
                <Link to="/" className="mob-rp__back">
                    ← Home
                </Link>
                <h1 className="mob-rp__title">Approach: useReducer + prefetch</h1>
                <p className="mob-rp__sub">
                    Same <code>POST /products</code> first page. State lives in one <code>useReducer</code>. After
                    load, image URLs are prefetched with a small concurrency pool. Tap the image area to
                    activate auto-advance; use View product for PDP.
                </p>
            </header>

            {state.loading ? (
                <ProductGridSkeleton cells={MOB_PRODUCTS_PAGE_SIZE} />
            ) : state.error ? (
                <p className="mob-rp__error" role="alert">
                    {state.error}
                </p>
            ) : (
                <div className="mob-rp__grid">
                    {state.items.map((product) => {
                        const isActive = state.activeId != null && String(state.activeId) === String(product.id);
                        const slide =
                            isActive ? state.slideById[product.id] ?? 0 : 0;
                        const src = product.urls[slide] || product.urls[0];

                        return (
                            <article key={product.id} className={`mob-rp-card${isActive ? ' mob-rp-card--active' : ''}`}>
                                <button
                                    type="button"
                                    className="mob-rp-card__viewport"
                                    onClick={() => dispatch({ type: 'ACTIVATE', id: product.id })}
                                    aria-pressed={isActive}
                                >
                                    {src ? (
                                        <img className="mob-rp-card__img" src={src} alt="" loading="lazy" />
                                    ) : null}
                                    {isActive && product.urls.length > 1 ? (
                                        <div className="mob-rp-card__dots" aria-hidden>
                                            {product.urls.map((_, i) => (
                                                <span
                                                    key={i}
                                                    className={`mob-rp-card__dot${i === slide ? ' is-on' : ''}`}
                                                />
                                            ))}
                                        </div>
                                    ) : null}
                                </button>
                                <div className="mob-rp-card__meta">
                                    <div className="mob-rp-card__brand">{product.brand ?? '—'}</div>
                                    {product.title ? (
                                        <div className="mob-rp-card__title">{product.title}</div>
                                    ) : null}
                                    <button
                                        type="button"
                                        className="mob-rp-card__pdp"
                                        onClick={() => goPdp(product.id)}
                                    >
                                        View product · Rs. {product.price ?? '—'}
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
